import React, { useMemo } from 'react';
import RouterNavLink from '@/components/mui/Router/RouterNavLink';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { useBlurAll } from '@/hooks/useBlurAll';
import { rtlEnable } from '@/domain/env';

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box, { BoxProps } from '@mui/material/Box';
import { Tabs as TabsMui } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tooltip from '@/components/mui/Tooltip';
import BetaBadge from '@/components/BetaBadge';
import { CollapseButton, TabsRootWrapper } from '@/components/Tabs/StyledComponents';

import CircleIcon from '@mui/icons-material/Circle';
import InfoIcon from '@mui/icons-material/Info';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

export function requiredTab(obj, fields) {
  return fields.some((key) => {
    const value = obj.get(key);
    return !value || value.length < 1;
  });
}

export interface TabEntry {
  id: string;
  defaultMessage: string;
  to: string;
  tabValue: string;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  Icon?: React.ElementType;
  helpTooltip?: React.ReactElement;
  betaBadge?: boolean;
  separator?: 'arrow' | 'divider';
  messageValues?: Record<string, string | number>;
}

type TabFactoryInput = Pick<TabEntry, 'id' | 'defaultMessage' | 'to' | 'tabValue'> & Partial<TabEntry>;

const DEFAULT_TAB: Partial<TabEntry> = {
  required: false,
  disabled: false,
  hidden: false,
};

export const TabFactory = (entry: TabFactoryInput): TabEntry => ({
  ...DEFAULT_TAB,
  ...entry,
});

export type TabsProps = {
  value: string;
  tabs: TabEntry[];
  title?: string;
  orientation?: 'horizontal' | 'vertical';
  textTransform?: 'uppercase' | 'none';
  enableCollapseMenu?: boolean;
  rootBoxProps?: BoxProps;
  tabsBoxProps?: BoxProps;
};

// MUI Tabs clones every child with tab props (value, selected, onChange...) - swallow them here,
// the element has no tabindex so keyboard navigation between tabs skips it
const TabSeparator = ({ type, isRtl }: { type: TabEntry['separator']; isRtl: boolean }) =>
  type === 'divider' ? (
    <Divider orientation="vertical" flexItem variant="middle" sx={{ mx: 1 }} aria-hidden />
  ) : (
    <Box display="flex" alignItems="center" color="text.secondary" aria-hidden>
      <ArrowRightAltIcon sx={{ fontSize: 24, transform: isRtl ? 'rotate(180deg)' : 'unset' }} />
    </Box>
  );

const Tabs: React.FC<TabsProps> = ({
  value,
  tabs,
  title,
  orientation = 'horizontal',
  textTransform = 'uppercase',
  enableCollapseMenu = false,
  rootBoxProps = {},
  tabsBoxProps = {},
}) => {
  const blurAll = useBlurAll();
  const isRtl = useSelector(rtlEnable);
  const { formatMessage } = useIntl();
  const [isMenuCollapsed, toggleIsMenuCollapsed] = useToggle(false);
  const availableTabs = useMemo(() => tabs.filter((tab) => !tab.hidden), [tabs]);

  const collapseMenuButtonTooltip = isMenuCollapsed
    ? formatMessage({ id: 'tooltip.menu.show', defaultMessage: 'Show menu' })
    : formatMessage({ id: 'tooltip.menu.hide', defaultMessage: 'Hide menu' });

  return (
    <TabsRootWrapper orientation={orientation} isCollapsed={isMenuCollapsed} {...rootBoxProps}>
      {!isMenuCollapsed && (
        <Box {...tabsBoxProps}>
          <>
            {!!title && (
              <Typography sx={{ px: 2, mb: 1 }} variant="subtitle2">
                {title}
              </Typography>
            )}
            <TabsMui value={value} orientation={orientation}>
              {availableTabs.flatMap(
                ({
                  id,
                  defaultMessage,
                  messageValues,
                  to,
                  tabValue,
                  required,
                  disabled,
                  Icon,
                  helpTooltip,
                  betaBadge,
                  separator,
                }) => [
                  <Tab
                    key={id}
                    value={tabValue}
                    icon={Icon ? <Icon /> : undefined}
                    iconPosition="start"
                    component={RouterNavLink}
                    disabled={disabled}
                    href={to}
                    sx={{
                      textTransform,
                      justifyContent: orientation === 'horizontal' ? 'center' : 'flex-start',
                      textAlign: 'initial',
                    }}
                    label={
                      <>
                        {formatMessage({ id, defaultMessage }, messageValues)}
                        {required && <CircleIcon sx={{ fontSize: 5, ml: 0.5, mb: 0.5 }} color="error" />}
                        {helpTooltip && (
                          <Tooltip t={helpTooltip} placement={isRtl ? 'left' : 'right'}>
                            <InfoIcon sx={{ fontSize: 20, ml: 'auto', pointerEvents: 'all' }} />
                          </Tooltip>
                        )}
                        {betaBadge && <BetaBadge sx={{ ml: 1 }} />}
                      </>
                    }
                  />,
                  ...(separator ? [<TabSeparator key={`${id}-separator`} type={separator} isRtl={isRtl} />] : []),
                ],
              )}
            </TabsMui>
          </>
        </Box>
      )}
      {enableCollapseMenu && (
        // TODO - [DA-16084] Add translations when we have it
        <Tooltip t={collapseMenuButtonTooltip} placement={isRtl ? 'left' : 'right'}>
          <CollapseButton
            onClick={() => {
              toggleIsMenuCollapsed();
              blurAll();
            }}
            sx={{ position: isMenuCollapsed ? 'unset' : 'absolute' }}
          >
            {isMenuCollapsed ? (
              <MenuIcon color="primary" sx={{ fontSize: 24 }} />
            ) : (
              <KeyboardArrowLeftIcon
                color="primary"
                sx={{ fontSize: 24, transform: isRtl ? 'rotate(180deg)' : 'unset' }}
              />
            )}
          </CollapseButton>
        </Tooltip>
      )}
    </TabsRootWrapper>
  );
};

export default Tabs;
